/**
 * AdminFirms — owner-only page listing every firm on the platform and
 * inline-creating new ones.  Gated by require_owner on the backend
 * (503 if OWNER_SUPABASE_UID unset, 403 if the caller isn't the owner).
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiV2 } from "@/lib/apiV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Plus, CaretRight, Warning } from "@phosphor-icons/react";

export default function AdminFirms() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [codePrefix, setCodePrefix] = useState("");
  const [ownerError, setOwnerError] = useState(null);

  // Firms list via a service-role admin endpoint isn't exposed; a fresh
  // firm is echoed back on POST /admin/firms.  We keep a local list in
  // component state, seeded from the last successful POSTs.  For a full
  // listing across sessions, add GET /admin/firms server-side.
  const list = useQuery({
    queryKey: ["admin-firms"],
    queryFn: async () => {
      // The firm dashboard endpoint returns memberships for the caller;
      // owners are seeded into every firm by convention or via /invite.
      const { data } = await apiV2.get("/firm/me").catch((e) => {
        if (e?.response?.status === 403) return { data: { memberships: [] } };
        throw e;
      });
      return data.memberships || [];
    },
  });

  useEffect(() => {
    // Proactively surface the "owner not configured" case so the admin
    // knows what to fix.
    apiV2.post("/admin/firms", { name: "__probe__", code_prefix: "AA" }).catch((e) => {
      if (e?.response?.status === 503) setOwnerError(e.response.data.detail);
    });
  }, []);

  const create = useMutation({
    mutationFn: async () => {
      const { data } = await apiV2.post("/admin/firms", {
        name: name.trim(),
        contact_name: contactName.trim() || undefined,
        contact_email: contactEmail.trim() || undefined,
        code_prefix: codePrefix.trim(),
      });
      return data;
    },
    onSuccess: (firm) => {
      toast.success(`Firm "${firm.name}" created`);
      setName("");
      setContactName("");
      setContactEmail("");
      setCodePrefix("");
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ["admin-firms"] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Could not create firm");
    },
  });

  return (
    <div className="space-y-6" data-testid="admin-firms-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#2C4C3B]">Firms</h1>
          <p className="text-sm text-slate-500">
            Accounting firms who buy blocks of MedEasy seats.
          </p>
        </div>
        <Button
          onClick={() => setShowForm((s) => !s)}
          className="bg-[#2C4C3B] hover:bg-[#22392C] text-white"
          data-testid="admin-firms-new-btn"
        >
          <Plus size={16} className="mr-1" />
          New firm
        </Button>
      </div>

      {ownerError && (
        <Card className="p-4 bg-amber-50 border-amber-200 flex gap-3">
          <Warning size={20} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Admin API disabled.</strong> {ownerError}
          </div>
        </Card>
      )}

      {showForm && (
        <Card className="p-6" data-testid="admin-firm-form">
          <h2 className="font-medium mb-4">Create firm</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Firm name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Kilbrick & Co."
                data-testid="firm-name-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Code prefix (2 letters, uppercase)</Label>
              <Input
                value={codePrefix}
                onChange={(e) => setCodePrefix(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="KB"
                maxLength={2}
                data-testid="firm-prefix-input"
                className="mt-1 uppercase font-mono"
              />
              <p className="text-xs text-slate-500 mt-1">
                No I, L, or O (ambiguous when read aloud).
              </p>
            </div>
            <div>
              <Label>Contact name (optional)</Label>
              <Input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Sarah Kilbrick"
                data-testid="firm-contact-name"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Contact email (optional)</Label>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="partner@kilbrick.ca"
                data-testid="firm-contact-email"
                className="mt-1"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={create.isPending || !name.trim() || codePrefix.length !== 2}
              className="bg-[#2C4C3B] hover:bg-[#22392C] text-white"
              data-testid="firm-create-submit"
            >
              {create.isPending ? "Creating…" : "Create firm"}
            </Button>
          </div>
        </Card>
      )}

      <Card className="divide-y divide-slate-100" data-testid="admin-firms-list">
        {list.isLoading ? (
          <div className="p-6 text-sm text-slate-500">Loading firms…</div>
        ) : (list.data || []).length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            No firms yet. Create your first one above.
          </div>
        ) : (
          list.data.map((f) => (
            <Link
              key={f.id}
              to={`/admin/firms/${f.id}`}
              className="p-4 flex items-center justify-between hover:bg-slate-50 no-underline"
              data-testid={`firm-row-${f.id}`}
            >
              <div>
                <div className="font-medium text-slate-900">{f.name || f.id}</div>
                <div className="text-xs text-slate-500 font-mono">
                  {f.code_prefix ? `${f.code_prefix}-XXXXXXXX` : ""}
                  {f.contact_email ? ` · ${f.contact_email}` : ""}
                </div>
              </div>
              <CaretRight size={18} className="text-slate-400" />
            </Link>
          ))
        )}
      </Card>
    </div>
  );
}
